/**
 * useSuggestionReveal: manages suggested-objectives state and staggered reveal.
 *
 * Handles three responsibilities:
 *   1. Syncing `suggestedObjectives` from the workspace cache when the active
 *      workspace changes.
 *   2. Staggering the initial reveal of the first three cards on
 *      OBJECTIVE_REVEAL_CADENCE_MS so the user watches the list build itself.
 *   3. Staggering extra-card reveal triggered by the "Show more" affordance.
 *
 * Critical fix: the workspace-switch reset effect clears BOTH `initialRevealTimersRef`
 * AND `showMoreTimersRef` before resetting counters. The original component only
 * cleared `initialRevealTimersRef`, leaving stale showMore timers that could fire
 * after a workspace switch and incorrectly increment `revealedExtraCount`.
 *
 * @module components/hooks/useSuggestionReveal
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import type { SuggestedObjective } from '../../lib/suggest-objectives';

/**
 * Min cadence between objective card reveals on the wizard's objectives step.
 * Mirrors the constant in CreateInterviewForm.tsx.
 */
const OBJECTIVE_REVEAL_CADENCE_MS = 600;

/**
 * Input props for the useSuggestionReveal hook. Uses primitives rather than
 * the full Workspace object to keep the hook pure and easy to test.
 */
export interface UseSuggestionRevealInput {
  /** Active workspace id. Used as the trigger for workspace-switch resets. */
  activeWorkspaceId: string | undefined;
  /**
   * ISO timestamp the suggestions cache was last written. Used as a dep
   * instead of the array reference so the reset only fires on meaningful
   * changes, not on every workspace re-fetch.
   */
  suggestedObjectivesGeneratedAt: string | null | undefined;
  /**
   * Cached objective suggestion cards from the active workspace. May be null
   * when the workspace has never had suggestions generated.
   */
  suggestedObjectivesCache: SuggestedObjective[] | null;
}

/**
 * Return value from the useSuggestionReveal hook.
 */
export interface UseSuggestionRevealResult {
  /** Current objective suggestions, or null if none are available. */
  suggestedObjectives: SuggestedObjective[] | null;
  /** Whether the user has triggered "Show more" to expand past the first 3. */
  showAllObjectives: boolean;
  /** How many of the first 3 cards have been revealed via stagger. */
  revealedInitialCount: number;
  /** How many extra cards (beyond the first 3) have been revealed via stagger. */
  revealedExtraCount: number;
  /**
   * Reveal extras past the first 3 with a staggered cadence so they drop in
   * one by one instead of appearing all at once.
   */
  handleShowMore: () => void;
}

/**
 * Manages suggested-objectives reveal state for the CreateInterviewForm wizard.
 *
 * The hook stagers card reveals so the user sees the list build itself rather
 * than a fully-populated panel popping in. It also fixes a bug in the original
 * component where switching workspaces while showMore timers were queued would
 * cause stale increments to fire for the new workspace.
 *
 * @param input - Workspace identifiers and cached objectives.
 * @returns Reveal state and the handleShowMore callback.
 */
export function useSuggestionReveal(input: UseSuggestionRevealInput): UseSuggestionRevealResult {
  const { activeWorkspaceId, suggestedObjectivesGeneratedAt, suggestedObjectivesCache } = input;

  const [suggestedObjectives, setSuggestedObjectives] = useState<SuggestedObjective[] | null>(null);
  const [showAllObjectives, setShowAllObjectives] = useState(false);
  const [revealedInitialCount, setRevealedInitialCount] = useState(0);
  const [revealedExtraCount, setRevealedExtraCount] = useState(0);

  /** Pending timers for the show-more extra reveal. Cancelled on workspace switch and unmount. */
  const showMoreTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  /** Pending timers for the initial 3-card stagger reveal. Cancelled on workspace switch and unmount. */
  const initialRevealTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  /**
   * Reset all reveal state when the active workspace changes. Critically this
   * clears BOTH timer sets to prevent stale timers from a previous workspace
   * incrementing counters for the new one.
   */
  useEffect(() => {
    for (const t of showMoreTimersRef.current) clearTimeout(t);
    showMoreTimersRef.current.clear();
    for (const t of initialRevealTimersRef.current) clearTimeout(t);
    initialRevealTimersRef.current.clear();

    setShowAllObjectives(false);
    setRevealedExtraCount(0);
    setRevealedInitialCount(0);

    const cached = suggestedObjectivesCache;
    setSuggestedObjectives(cached && cached.length > 0 ? cached : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWorkspaceId, suggestedObjectivesGeneratedAt]);

  /**
   * Stagger the initial reveal of up to 3 cards when a fresh suggestions array
   * lands. Each card drops in OBJECTIVE_REVEAL_CADENCE_MS after the previous.
   */
  useEffect(() => {
    if (suggestedObjectives === null) return;
    for (const t of initialRevealTimersRef.current) clearTimeout(t);
    initialRevealTimersRef.current.clear();
    setRevealedInitialCount(0);

    const visible = Math.min(3, suggestedObjectives.length);
    for (let i = 1; i <= visible; i++) {
      const target = i;
      const timer = setTimeout(() => {
        initialRevealTimersRef.current.delete(timer);
        setRevealedInitialCount((prev) => Math.max(prev, target));
      }, i * OBJECTIVE_REVEAL_CADENCE_MS);
      initialRevealTimersRef.current.add(timer);
    }
  }, [suggestedObjectives]);

  /**
   * Reveal rows hidden behind "Show more" with a staggered cadence so extras
   * read as freshly-generated options rather than a blunt list expansion.
   */
  const handleShowMore = useCallback(() => {
    if (!suggestedObjectives) return;
    setShowAllObjectives(true);
    const extras = Math.max(0, suggestedObjectives.length - 3);
    for (let i = 1; i <= extras; i++) {
      const target = i;
      const timer = setTimeout(() => {
        showMoreTimersRef.current.delete(timer);
        setRevealedExtraCount((prev) => Math.max(prev, target));
      }, i * OBJECTIVE_REVEAL_CADENCE_MS);
      showMoreTimersRef.current.add(timer);
    }
  }, [suggestedObjectives]);

  /** Cancel all pending timers on unmount to prevent state updates on an unmounted hook. */
  useEffect(() => {
    const showMore = showMoreTimersRef.current;
    const initial = initialRevealTimersRef.current;
    return () => {
      for (const t of showMore) clearTimeout(t);
      showMore.clear();
      for (const t of initial) clearTimeout(t);
      initial.clear();
    };
  }, []);

  return {
    suggestedObjectives,
    showAllObjectives,
    revealedInitialCount,
    revealedExtraCount,
    handleShowMore,
  };
}
